import { INSIGHTS_API_ENDPOINT, adminAuthHeaders, getAdminToken, setAdminToken } from ".";

export type InsightCollection = "ARTICLE" | "WEEKLY" | "EVENT";
export type EditorMode = "LINKEDIN" | "CODE";
export type PublishStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface InsightApiData {
  id: string;
  slug: string;
  title: string;
  sub: string;
  preview: string;
  bodyHtml: string;
  editorMode: EditorMode;
  date: string;
  isoDate: string;
  collection: InsightCollection;
  seo: {
    pageTitle: string;
    metaDescription: string;
    keyWords: string;
  };
  img: {
    src: string;
    alt: string;
  };
  status: PublishStatus;
  createdAt?: string;
  updatedAt?: string;
  stories?: WeeklyStoryItem[];
}

export interface InsightsPage {
  items: InsightApiData[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface WeeklyStoryItem {
  id: string;
  order: number;
  title: string;
  category: string;
  countries: string;
  summary: string;
  commentary: string;
}

export interface WeeklyTimelineItem {
  id: string;
  title: string;
  category: string;
  countries: string[];
  summary: string;
  commentary: string;
  issue: {
    slug: string;
    title: string;
    date: string;
    isoDate: string;
  };
}

export const WEEKLY_STORY_CATEGORIES = [
  "Policy Announcement",
  "Project & Construction",
  "Partnership & Collaboration",
  "Investment & Finance",
  "Technology & Engineering",
  "Supply Chain & Manufacturing",
  "Market & Trade",
  "Milestone & Event",
] as const;

const COLLECTION_QUERY: Record<string, string> = {
  articles: "articles",
  weekly: "weekly",
  events: "events",
};

export async function fetchInsightsPage(
  collection?: "articles" | "weekly" | "events",
  page = 1,
  pageSize = 12
): Promise<InsightsPage> {
  const params = new URLSearchParams();
  if (collection) params.set("collection", COLLECTION_QUERY[collection]);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));

  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/insights?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to fetch insights");
  return res.json();
}

export async function fetchWeeklyTimeline(): Promise<WeeklyTimelineItem[]> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/insights/weekly/timeline`);
  if (!res.ok) throw new Error("Failed to fetch weekly timeline");
  const data = await res.json();
  return data.items;
}

export async function fetchInsightBySlug(
  slug: string
): Promise<InsightApiData | null> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/insights/${slug}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to fetch insight");
  return res.json();
}

const jsonHeaders = { "Content-Type": "application/json" };

export async function adminLogin(
  email: string,
  password: string
): Promise<{ email: string }> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/auth/login`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Invalid email or password");
  }
  const data = await res.json();
  setAdminToken(data.token);
  return { email: data.email };
}

export async function adminLogout(): Promise<void> {
  setAdminToken(null);
}

export async function adminMe(): Promise<{ email: string } | null> {
  if (!getAdminToken()) return null;
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/auth/me`, {
    headers: adminAuthHeaders(),
  });
  if (!res.ok) {
    if (res.status === 401) setAdminToken(null);
    return null;
  }
  return res.json();
}

export async function fetchAdminInsights(): Promise<InsightApiData[]> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/insights`, {
    headers: adminAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch admin insights");
  return res.json();
}

export async function fetchAdminInsight(id: string): Promise<InsightApiData> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/insights/${id}`, {
    headers: adminAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch insight");
  return res.json();
}

export async function createInsight(
  data: Partial<InsightApiData> & { collection: InsightCollection }
): Promise<InsightApiData> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/insights`, {
    method: "POST",
    headers: { ...jsonHeaders, ...adminAuthHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create insight");
  return res.json();
}

export async function updateInsight(
  id: string,
  data: Partial<InsightApiData>
): Promise<InsightApiData> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/insights/${id}`, {
    method: "PUT",
    headers: { ...jsonHeaders, ...adminAuthHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update insight");
  return res.json();
}

export async function deleteInsight(id: string): Promise<void> {
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/insights/${id}`, {
    method: "DELETE",
    headers: adminAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete insight");
}

export async function uploadImage(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${INSIGHTS_API_ENDPOINT}/admin/uploads`, {
    method: "POST",
    headers: adminAuthHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Failed to upload image");
  }
  return res.json();
}
